import { useEffect, useMemo, useState } from 'react';
import { hesselApiTypes } from '../../lib/api/models/hessel-api';
import { LeadDurationDeliveryStore } from '../../lib/state/hessel-site/lead-checkout/duration-delivery';
import { LeadCheckoutFormsStore } from '../../lib/state/hessel-site/lead-checkout/forms';
import { generateHireFormId } from '../../lib/state/hessel-site/lead-checkout/forms/hire-forms.helpers';
import { LeadCheckoutStepsStore } from '../../lib/state/hessel-site/lead-checkout/steps';
import { ProductDetailsStore } from '../../lib/state/hessel-site/product-details';
import { hesselViewModels } from '../../lib/view-models';
import { DateStyle, formatDate, formInputValue } from '../../utils/helpers';
import { useRouter } from 'next/router';

export const useSelectedLeasingCheckoutData = (): hesselApiTypes.SelectedLeasingCheckoutSubmit | undefined => {
    const [checkoutData, setCheckoutData] = useState<hesselApiTypes.SelectedLeasingCheckoutSubmit>();

    const router = useRouter();

    // Lead Checkout Steps Store
    const { steps } = LeadCheckoutStepsStore.useStoreState((state) => state);
    const { inputs, checkoutMode } = LeadCheckoutFormsStore.useStoreState((state) => state);

    // Duration & Delivery Store
    const { termsAccepted, selectedDepartment, comment } = LeadDurationDeliveryStore.useStoreState((state) => state);

    // Forms values (Step 1)
    const { city, email, familyName, givenName, mobile, postalCode, streetName } = useMemo(() => {
        const address = inputs
            .map(
                (input) =>
                    input.id === generateHireFormId('checkoutStepUserInformation', 'Address') && input.type === 'address' ? input.inputs : undefined,
                []
            )
            .filter((val) => (val ? true : false))
            .reduce((acc, val) => (val ? [...(acc ? acc : []), ...val] : acc), []);

        const cityInput = address?.find(({ id }) => id === generateHireFormId('checkoutStepUserInformation', 'Address', 'city'));
        const zipCodeInput = address?.find(({ id }) => id === generateHireFormId('checkoutStepUserInformation', 'Address', 'zip'));
        const streetInput = address?.find(({ id }) => id === generateHireFormId('checkoutStepUserInformation', 'Address', 'address'));

        const emailInput = inputs.find(({ id }) => id === generateHireFormId('checkoutStepUserInformation', 'Email'));
        const mobileInput = inputs.find(({ id }) => id === generateHireFormId('checkoutStepUserInformation', 'Telephone number'));

        const nameInput = inputs.find(({ id }) => id === generateHireFormId('checkoutStepUserInformation', 'Name'));
        const [familyName, ...reversed] = (nameInput ? formInputValue(nameInput).trim() : '').split(' ').reverse();

        return {
            city: cityInput ? formInputValue(cityInput) : '',
            postalCode: zipCodeInput ? formInputValue(zipCodeInput) : '',
            streetName: streetInput ? formInputValue(streetInput) : '',
            email: emailInput ? formInputValue(emailInput) : '',
            mobile: mobileInput ? formInputValue(mobileInput) : '',
            givenName: reversed.reverse().join(' '),
            familyName,
        };
    }, [inputs]);

    // Duration & Delivery step: Consent data
    const consentText = useMemo(
        () =>
            steps
                .map((step) => (step.type === 'checkoutStepDurationAndDelivery' ? step.content.termsContent : ''))
                .find((content) => (content ? true : false)),
        [steps]
    );

    const consentCode = useMemo(
        () =>
            steps
                .map((step) => (step.type === 'checkoutStepDurationAndDelivery' ? step.content.termsCode : 0))
                .find((content) => (content ? true : false)),
        [steps]
    );

    // Product Details Store
    const { selectedProduct, selectedStartupDate, selectedColor } = ProductDetailsStore.useStoreState((state) => state);

    const vehicleImageUrl = useMemo(() => {
        const matchedResource = selectedProduct?.resources.filter((x) => x.type === 'image' && x.colorCode === selectedColor?.code);

        if (!matchedResource || matchedResource.length === 0) {
            return (selectedProduct?.resources?.find((x) => x.type === 'image') as hesselViewModels.Image)?.url ?? '';
        }

        const matched = matchedResource[0] as hesselViewModels.Image;
        return matched.url.replace('productdetailsfullsize', 'email').replace('{IMAGE-SIZE}', 'email');
    }, [selectedColor?.code, selectedProduct?.resources]);

    const addOns = useMemo(
        () =>
            selectedProduct?.optionalEquipment?.['HiRE Selected'].map(({ id, name, monthlyHireSelectedPrice, isSelected, types }) => {
                return {
                    id,
                    name,
                    price: monthlyHireSelectedPrice ?? 0,
                    selected: isSelected ?? false,
                    isInsurance: types?.includes('Forsikring') ?? false,
                };
            }),
        [selectedProduct]
    );

    const totalMonthlyPrice = useMemo(() => {
        const totalPriceWithMoms = selectedProduct?.purchaseTypes?.hireSelected?.baseMonthlyPrice ?? 0;
        const totalAddonsPrice = addOns?.reduce((acc, { price, selected }) => (selected ? acc + price : acc), 0) ?? 0;
        const totalPrice = totalPriceWithMoms + totalAddonsPrice;
        return totalPrice;
    }, [addOns, selectedProduct]);

    useEffect(() => {
        const prices = selectedProduct?.purchaseTypes.hireSelected;
        if (!prices) return;
        setCheckoutData({
            name: `${givenName} ${familyName}`,
            email: email,
            phone: mobile,
            address: streetName,
            postalCode: +postalCode,
            city: city,
            customerType: 'B2C',
            departmentId: Number(selectedDepartment?.hovedafdelingId) ?? null,
            departmentName: selectedDepartment?.name ?? '',
            url: router.asPath,
            vehicle: {
                vehicleId: selectedProduct?.entityId ?? 0,
                referenceNumber: selectedProduct?.referenceNumber ?? '-',
                brand: selectedProduct?.brand ?? '',
                model: selectedProduct?.modelTitle ?? '',
                variant: selectedProduct?.variantTitle ?? '',
                price: totalMonthlyPrice,
                color: selectedColor?.name ?? '',
                vehicleImageUrl: vehicleImageUrl,
                url: selectedProduct?.url ?? '',
                type: selectedProduct?.vehicleType ?? '',
                availability: selectedProduct?.availability ?? '',
                modelYear: Number(selectedProduct?.modelYear) ?? null,
                fuelConsumption: selectedProduct?.specifications.fuelConsumptionForSelectedLead,
                mileage: selectedProduct?.specifications.odometerReadingInKm ?? 0,
                greenOwnerFee: Number(selectedProduct?.specifications.greenOwnershipFee) ?? null,
            },
            addons: addOns ?? [],
            consent: termsAccepted ?? false,
            consentText: consentText ?? '',
            leasingStart: formatDate(selectedStartupDate ?? 0, DateStyle.booking_format),
            durationMonths: selectedProduct.purchaseTypes.hireSelected?.durationMonths ?? 0,
            monthlyPrice: totalMonthlyPrice,
            initialPayment: prices.initialPayment ?? 0,
            returnFee: prices.returnFee ?? 0,
            insuranceOptOutFee: prices.insuranceOptOutFee ?? 0,
            establishmentFee: prices.establishmentFee ?? 0,
            documentFee: prices.documentFee ?? 0,
            kmPerMonth: prices.kilometersPerYear ? Math.round(prices.kilometersPerYear / 12) : 0,
            kmPerYear: prices.kilometersPerYear ?? 0,
            overrunKmPrice: prices.excessiveMileageRate ?? 0,
            comment: comment ?? null,
        });
    }, [
        checkoutMode,
        city,
        consentCode,
        consentText,
        email,
        familyName,
        givenName,
        mobile,
        postalCode,
        selectedColor,
        selectedDepartment,
        selectedStartupDate,
        streetName,
        termsAccepted,
        vehicleImageUrl,
        totalMonthlyPrice,
        selectedProduct,
        addOns,
        router.asPath,
        comment,
    ]);

    return checkoutData;
};
